/**
 * creditLedgerService — Records operational cost debits to the credit_ledger table.
 *
 * IMPORTANT: These are internal infrastructure costs. Users are NEVER shown
 * raw cost figures. Only aggregated "consumption" summaries appear in invoices
 * (e.g. "3 resume optimisations", "15 job searches") — never prices per unit.
 *
 * Call debitCredits() from any feature that consumes paid infrastructure.
 * Failures are silently swallowed so they never break the user-facing flow.
 */

import { supabase } from '../lib/supabase';

// ── Category constants ────────────────────────────────────────────────────────

export type LedgerCategory =
  | 'pinecone'       // Vector search / upsert (Pinecone)
  | 'llm'            // LLM API calls (Groq, OpenAI)
  | 'agentic'        // Full agentic resume / cover-letter flows
  | 'infrastructure' // Storage, CDN, email sends, compute
  | 'marketing'      // Blog, campaign emails, referrals
  | 'sales';         // Lead enrichment, outbound, CRM

// ── Unit cost table (internal; NEVER exposed to users) ────────────────────────
// Values are in fractional "platform credits" (1 credit ≈ ₹1 internal cost).

const UNIT_COSTS: Record<string, number> = {
  resume_optimize:          0.08,  // per full agentic rewrite
  resume_section_optimize:  0.012, // per section LLM call
  cover_letter_generate:    0.06,  // per cover letter
  interview_questions:      0.04,  // per question set
  job_vector_search:        0.002, // per Pinecone similarity query
  job_vector_upsert:        0.001, // per Pinecone vector write
  llm_api_call:             0.005, // generic LLM call
  email_send:               0.001, // transactional email
  resume_email_send:        0.002, // resume delivery email
  storage_write:            0.0005,// file upload / write
  skill_assessment:         0.03,  // AI skill quiz generation + scoring
  match_explanation:        0.008, // job match explanation LLM call
  blog_publish:             0.01,  // blog post AI processing
  lead_enrichment:          0.05,  // Exa/Apollo lead lookup
  referral_attribution:     0.002, // referral tracking
};

// ── Core debit function ───────────────────────────────────────────────────────

export interface DebitParams {
  category: LedgerCategory;
  operation: keyof typeof UNIT_COSTS | string;
  units?: number;
  description?: string;
  metadata?: Record<string, unknown>;
  userId?: string; // optional override (uses current session by default)
}

/**
 * Record a cost debit. Fire-and-forget — never throws, never blocks the caller.
 */
export async function debitCredits(params: DebitParams): Promise<void> {
  try {
    let uid = params.userId;
    if (!uid) {
      const { data: { user } } = await supabase.auth.getUser();
      uid = user?.id;
    }
    if (!uid) return; // anonymous users — no ledger entry

    const unitCost = UNIT_COSTS[params.operation] ?? 0;
    const units = params.units ?? 1;

    await supabase.from('credit_ledger').insert({
      user_id:     uid,
      category:    params.category,
      operation:   params.operation,
      units,
      unit_cost:   unitCost,
      description: params.description ?? null,
      metadata:    params.metadata ?? {},
    });
  } catch {
    // Silently swallow — cost tracking must never break user flows
  }
}

// ── Convenience wrappers ──────────────────────────────────────────────────────

export const ledger = {
  resumeOptimize: (meta?: Record<string, unknown>) =>
    debitCredits({ category: 'agentic', operation: 'resume_optimize', description: 'Full resume optimisation', metadata: meta }),

  resumeSectionOptimize: (section: string, meta?: Record<string, unknown>) =>
    debitCredits({ category: 'llm', operation: 'resume_section_optimize', description: `Section: ${section}`, metadata: meta }),

  coverLetterGenerate: (meta?: Record<string, unknown>) =>
    debitCredits({ category: 'agentic', operation: 'cover_letter_generate', description: 'Cover letter generation', metadata: meta }),

  interviewQuestions: (meta?: Record<string, unknown>) =>
    debitCredits({ category: 'agentic', operation: 'interview_questions', description: 'Interview questions', metadata: meta }),

  jobVectorSearch: (meta?: Record<string, unknown>) =>
    debitCredits({ category: 'pinecone', operation: 'job_vector_search', description: 'Job match vector search', metadata: meta }),

  jobVectorUpsert: (units = 1, meta?: Record<string, unknown>) =>
    debitCredits({ category: 'pinecone', operation: 'job_vector_upsert', units, description: 'Job vector upsert', metadata: meta }),

  emailSend: (type = 'transactional', meta?: Record<string, unknown>) =>
    debitCredits({ category: 'infrastructure', operation: type === 'resume' ? 'resume_email_send' : 'email_send', description: `Email: ${type}`, metadata: meta }),

  skillAssessment: (meta?: Record<string, unknown>) =>
    debitCredits({ category: 'agentic', operation: 'skill_assessment', description: 'Skill assessment', metadata: meta }),

  matchExplanation: (meta?: Record<string, unknown>) =>
    debitCredits({ category: 'llm', operation: 'match_explanation', description: 'Job match explanation', metadata: meta }),

  blogPublish: (meta?: Record<string, unknown>) =>
    debitCredits({ category: 'marketing', operation: 'blog_publish', description: 'Blog post publish', metadata: meta }),

  leadEnrichment: (meta?: Record<string, unknown>) =>
    debitCredits({ category: 'sales', operation: 'lead_enrichment', description: 'Lead enrichment', metadata: meta }),
};

// ── Usage summary query ───────────────────────────────────────────────────────

export interface UsageSummaryRow {
  category: LedgerCategory;
  operation: string;
  total_units: number;
  total_debit: number;
  event_count: number;
}

/** Category-level display labels shown in invoice consumption summary */
export const CATEGORY_LABELS: Record<LedgerCategory, string> = {
  pinecone:       'Vector Search (Pinecone)',
  llm:            'AI Processing (LLM)',
  agentic:        'Agentic Features',
  infrastructure: 'Infrastructure',
  marketing:      'Marketing',
  sales:          'Sales & Outreach',
};

/** Operation-level display labels (user-friendly, no cost figures) */
export const OPERATION_LABELS: Record<string, string> = {
  resume_optimize:         'Resume optimisations',
  resume_section_optimize: 'Resume section refinements',
  cover_letter_generate:   'Cover letters generated',
  interview_questions:     'Interview question sets',
  job_vector_search:       'Job match searches',
  job_vector_upsert:       'Job vectors indexed',
  llm_api_call:            'AI calls',
  email_send:              'Emails sent',
  resume_email_send:       'Resume emails sent',
  storage_write:           'File uploads',
  skill_assessment:        'Skill assessments',
  match_explanation:       'Match explanations',
  blog_publish:            'Blog posts published',
  lead_enrichment:         'Leads enriched',
  referral_attribution:    'Referrals tracked',
};

export async function getUsageSummary(
  from?: Date,
  to?: Date,
): Promise<UsageSummaryRow[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase.rpc('get_credit_usage_summary', {
      p_user_id: user.id,
      p_from:    from?.toISOString() ?? new Date(Date.now() - 30 * 86400_000).toISOString(),
      p_to:      to?.toISOString()   ?? new Date().toISOString(),
    });

    if (error) {
      console.warn('[creditLedger] usage summary error:', error.message);
      return [];
    }

    return (data ?? []) as UsageSummaryRow[];
  } catch {
    return [];
  }
}
